import Konva from 'konva';
import type { Filter, KonvaEventObject } from 'konva/lib/Node';
import React, { FC, useEffect, useLayoutEffect, useRef, useState } from 'react';
import { Ellipse, Group, Image as KonvaImage, Line, Rect, Star, Text } from 'react-konva';

import { cssFontFamily } from '../../config/fonts';
import { AdElement, ImageElement, ShapeElement, TextElement } from '../../model/types';
import { useImageElement } from '../../utils/image';
import { containBox, coverCrop, imageCornerRadius, trianglePoints } from './shapeGeometry';

export interface SceneNodeProps {
  element: AdElement;
  /** `additive` = Shift/Ctrl przy kliknięciu, czyli dokładanie do zaznaczenia. */
  onSelect: (id: string, additive: boolean) => void;
  onDragStart: (event: KonvaEventObject<DragEvent>) => void;
  onDragMove: (event: KonvaEventObject<DragEvent>) => void;
  onDragEnd: (event: KonvaEventObject<DragEvent>) => void;
  onTransformStart: () => void;
  /** Wołane w trakcie skalowania, wykorzystywane przez tekst do przelewania wierszy. */
  onTransform?: (event: KonvaEventObject<Event>) => void;
  onTransformEnd: (event: KonvaEventObject<Event>) => void;
  onEditText: (id: string) => void;
  /** Podwójne kliknięcie pustej ramki zdjęcia; otwiera wybór pliku. */
  onPickImage?: (id: string) => void;
  /** Prawy przycisk myszy na elemencie; otwiera menu podręczne. */
  onContextMenu?: (event: KonvaEventObject<PointerEvent>) => void;
  /** Zwraca zmierzoną wysokość tekstu, żeby model nadążał za automatycznym łamaniem wierszy. */
  onMeasure: (height: number) => void;
  /** Ukrywa węzeł na czas edycji tekstu w nakładce HTML. */
  isEditing: boolean;
}

const shadowProps = (element: AdElement) => ({
  shadowEnabled: Boolean(element.shadow),
  shadowColor: element.shadow?.color ?? '#000000',
  shadowBlur: element.shadow?.blur ?? 0,
  shadowOffsetX: element.shadow?.offsetX ?? 0,
  shadowOffsetY: element.shadow?.offsetY ?? 0,
});

const konvaFontStyle = (element: TextElement): string => (
  `${element.fontStyle === 'italic' ? 'italic ' : ''}${element.fontWeight}`
);

const TextNode: FC<{ element: TextElement; isEditing: boolean; onMeasure: (height: number) => void }> = ({
  element,
  isEditing,
  onMeasure,
}) => {
  const textRef = useRef<Konva.Text>(null);
  const [measuredHeight, setMeasuredHeight] = useState(element.fontSize * element.lineHeight);

  useLayoutEffect(() => {
    const node = textRef.current;
    if (!node) return;
    const height = node.height();
    setMeasuredHeight(height);
    onMeasure(Math.round(height + element.padding * 2));
  }, [
    element.text,
    element.fontSize,
    element.width,
    element.fontFamily,
    element.fontWeight,
    element.fontStyle,
    element.lineHeight,
    element.letterSpacing,
    element.uppercase,
    element.padding,
  ]);

  const innerWidth = Math.max(1, element.width - element.padding * 2);

  /**
   * Przy dopasowaniu do ramki zmniejszamy stopień pisma proporcjonalnie do
   * przekroczenia wysokości. Jedna korekta wystarcza, bo wysokość tekstu jest
   * niemal liniowa względem stopnia — a pętla z pomiarem w każdej iteracji
   * kosztowałaby przerysowanie planszy przy każdym naciśnięciu klawisza.
   */
  const boxHeight = Math.max(1, element.height - element.padding * 2);
  const renderFontSize = element.autoFit && measuredHeight > boxHeight
    ? Math.max(8, Math.floor(element.fontSize * (boxHeight / measuredHeight)))
    : element.fontSize;

  return (
    <>
      {Boolean(element.background) && (
        <Rect
          width={element.width}
          height={(element.autoFit ? boxHeight : measuredHeight) + element.padding * 2}
          fill={element.background}
          cornerRadius={element.backgroundRadius}
          {...shadowProps(element)}
        />
      )}
      <Text
        ref={textRef}
        x={element.padding}
        y={element.padding}
        width={innerWidth}
        text={element.uppercase ? element.text.toUpperCase() : element.text}
        fontSize={renderFontSize}
        fontFamily={cssFontFamily(element.fontFamily)}
        fontStyle={konvaFontStyle(element)}
        fill={element.color}
        align={element.align}
        lineHeight={element.lineHeight}
        letterSpacing={element.letterSpacing}
        stroke={element.stroke || undefined}
        strokeWidth={element.stroke ? element.strokeWidth : 0}
        fillAfterStrokeEnabled
        wrap="word"
        visible={!isEditing}
        {...(element.background ? {} : shadowProps(element))}
      />
    </>
  );
};

const ShapeNode: FC<{ element: ShapeElement }> = ({ element }) => {
  const common = {
    fill: element.fill || undefined,
    stroke: element.stroke || undefined,
    strokeWidth: element.stroke ? element.strokeWidth : 0,
    ...shadowProps(element),
  };

  if (element.shape === 'ellipse') {
    return (
      <Ellipse
        x={element.width / 2}
        y={element.height / 2}
        radiusX={element.width / 2}
        radiusY={element.height / 2}
        {...common}
      />
    );
  }

  if (element.shape === 'triangle') {
    return <Line points={trianglePoints(element.width, element.height)} closed {...common} />;
  }

  if (element.shape === 'star') {
    const outerRadius = Math.min(element.width, element.height) / 2;
    return (
      <Star
        x={element.width / 2}
        y={element.height / 2}
        numPoints={5}
        innerRadius={outerRadius * 0.45}
        outerRadius={outerRadius}
        {...common}
      />
    );
  }

  return (
    <Rect
      width={element.width}
      height={element.height}
      cornerRadius={element.shape === 'line' ? element.height / 2 : element.radius}
      {...common}
    />
  );
};

/** Filtry Konvy wymagają buforowania węzła, inaczej nie mają czego przetworzyć. */
const useCachedFilters = (
  ref: React.RefObject<Konva.Image | null>,
  element: ImageElement,
  ready: boolean,
): Filter[] => {
  const filters: Filter[] = [];
  if (element.blur) filters.push(Konva.Filters.Blur);
  if (element.brightness) filters.push(Konva.Filters.Brighten);
  if (element.contrast) filters.push(Konva.Filters.Contrast);
  if (element.grayscale) filters.push(Konva.Filters.Grayscale);

  useEffect(() => {
    const node = ref.current;
    if (!node || !ready) return;
    if (filters.length === 0) {
      node.clearCache();
      return;
    }
    node.cache();
    node.getLayer()?.batchDraw();
  }, [
    ref,
    ready,
    filters.length,
    element.blur,
    element.brightness,
    element.contrast,
    element.grayscale,
    element.width,
    element.height,
    element.src,
    element.radius,
    element.circle,
    element.fit,
  ]);

  return filters;
};

const ImageNode: FC<{ element: ImageElement }> = ({ element }) => {
  const image = useImageElement(element.src);
  const imageRef = useRef<Konva.Image>(null);
  const filters = useCachedFilters(imageRef, element, Boolean(image));

  if (!image) {
    return (
      <Rect
        width={element.width}
        height={element.height}
        fill="#e4e4e7"
        cornerRadius={imageCornerRadius(element)}
      />
    );
  }

  const filterProps = {
    filters: filters.length > 0 ? filters : undefined,
    blurRadius: element.blur ?? 0,
    brightness: element.brightness ?? 0,
    contrast: (element.contrast ?? 0) * 100,
  };

  if (element.fit === 'contain') {
    const box = containBox(image, element.width, element.height);
    return (
      <KonvaImage
        ref={imageRef}
        image={image}
        x={box.x}
        y={box.y}
        width={box.width}
        height={box.height}
        cornerRadius={imageCornerRadius(element)}
        {...filterProps}
        {...shadowProps(element)}
      />
    );
  }

  return (
    <KonvaImage
      ref={imageRef}
      image={image}
      width={element.width}
      height={element.height}
      crop={coverCrop(image, element.width, element.height, element.focusX, element.focusY)}
      cornerRadius={imageCornerRadius(element)}
      {...filterProps}
      {...shadowProps(element)}
    />
  );
};

/**
 * Każdy element sceny to grupa ustawiona w punkcie (x, y). Transformer skaluje
 * grupę, a my zamieniamy tę skalę na konkretne wymiary w modelu, żeby tekst i
 * obrys nie rozjeżdżały się przy kolejnych zmianach rozmiaru.
 */
export const SceneNode: FC<SceneNodeProps> = ({
  element,
  onSelect,
  onDragStart,
  onDragMove,
  onDragEnd,
  onTransformStart,
  onTransform,
  onTransformEnd,
  onEditText,
  onMeasure,
  onContextMenu,
  onPickImage,
  isEditing,
}) => {
  if (element.hidden) return null;

  /**
   * Podwójne kliknięcie: tekst wchodzi w edycję treści, a pusta ramka zdjęcia
   * otwiera wybór pliku. Ramka z już wstawionym zdjęciem nic nie robi, żeby
   * przypadkowy dubel nie podmieniał materiału.
   */
  const handleDoubleClick = (): void => {
    if (element.type === 'text') {
      onEditText(element.id);
      return;
    }
    if (element.type === 'image' && !element.src) onPickImage?.(element.id);
  };

  const handleSelect = (event: KonvaEventObject<MouseEvent | TouchEvent>): void => {
    const evt = event.evt as MouseEvent;
    onSelect(element.id, Boolean(evt?.shiftKey || evt?.metaKey || evt?.ctrlKey));
  };

  return (
    <Group
      id={element.id}
      name="scene-element"
      x={element.x}
      y={element.y}
      rotation={element.rotation}
      opacity={element.opacity}
      draggable={!element.locked}
      listening={!element.locked}
      onMouseDown={handleSelect}
      onTouchStart={handleSelect}
      onDragStart={onDragStart}
      onDragMove={onDragMove}
      onDragEnd={onDragEnd}
      onTransformStart={onTransformStart}
      onTransform={onTransform}
      onTransformEnd={onTransformEnd}
      onContextMenu={onContextMenu}
      onDblClick={handleDoubleClick}
      onDblTap={handleDoubleClick}
    >
      {element.type === 'text' && <TextNode element={element} isEditing={isEditing} onMeasure={onMeasure} />}
      {element.type === 'shape' && <ShapeNode element={element} />}
      {element.type === 'image' && <ImageNode element={element} />}
    </Group>
  );
};
